import { useState } from 'react';
import { DoctorAvailabilityData, DateAvailability } from 'data-types';

type SelectedTime = {
    start: string;
    end: string;
} | null;

interface UseVideoConsultationAppointmentReturn {
    selectedDoctor: DoctorAvailabilityData | null;
    setSelectedDoctor: (doctor: DoctorAvailabilityData | null) => void;
    selectedDate: DateAvailability | null;
    setSelectedDate: (date: DateAvailability | null) => void;
    selectedTime: SelectedTime;
    setSelectedTime: (time: SelectedTime) => void;
    handleSelectDoctor: (doctorAvailabilityData: DoctorAvailabilityData) => void;
    handleDateSelection: (dateString: string) => void;
    doctorsAvailabilityData: DoctorAvailabilityData[];
    setDoctorsAvailabilityData: (doctorsAvailabilityData: DoctorAvailabilityData[]) => void;
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
    allowSubmit: boolean;
}

/**
 * Custom hook to manage video consultation appointment scheduling state and logic
 * Handles doctor selection, date selection, time selection, and short notice disclaimer
 */
export const useVideoConsultationAppointment = (): UseVideoConsultationAppointmentReturn => {
  const [doctorsAvailabilityData, setDoctorsAvailabilityData] = useState<DoctorAvailabilityData[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorAvailabilityData | null>(null);
  const [selectedDate, setSelectedDate] = useState<DateAvailability | null>(null);
  const [selectedTime, setSelectedTime] = useState<SelectedTime>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Allow submit when date and time are selected, even without doctor (for preferred time)
  const allowSubmit = Boolean(selectedDate && selectedTime && !isSubmitting);


  // handle selected doctor
  const handleSelectDoctor = (doctorAvailabilityData: DoctorAvailabilityData) => {
    if (doctorAvailabilityData.doctorID === selectedDoctor?.doctorID) {
      return;
    }

    setSelectedDoctor(doctorAvailabilityData);
    
    // default to first date and time that are available
    const defaultSelectedDate = doctorAvailabilityData.availability[0];
    setSelectedDate(defaultSelectedDate);

    // reset the selected time
    setSelectedTime(null);
  }


  // handle selected date
  const handleDateSelection = (dateString: string) => {
    // For preferred time (no doctor), create a simple date availability object
    if (!selectedDoctor) {
      setSelectedDate({ date: dateString, timeslots: [] });
      return;
    }

    // Find the DateAvailability object for the selected date
    const dateAvailabilities = selectedDoctor.availability.find(da => da.date === dateString);
    
    if (!dateAvailabilities) {
        return;
    }

    // set the selected date
    setSelectedDate(dateAvailabilities);

    if (dateAvailabilities.date !== selectedDate?.date) {
      // reset the selected time
      setSelectedTime(null);
    }
  };

  
  return {
    selectedDoctor,
    setSelectedDoctor,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    handleSelectDoctor,
    handleDateSelection,
    doctorsAvailabilityData,
    setDoctorsAvailabilityData,
    isSubmitting,
    setIsSubmitting,
    allowSubmit,
  }
};
